import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import AddCustomer from 'sections/apps/customer/AddCustomer';
import AuthGuard from 'utils/route-guard/AuthGuard';
import CatalogueGuard from 'utils/route-guard/CatalogueGuard';
import GuidedTour from 'components/GuidedTour';
import ImpersonationBanner from 'components/ImpersonationBanner';
import FeedbackWidget from 'components/FeedbackWidget';
import { TourProvider } from 'contexts/TourContext';
import { FeedbackProvider } from 'contexts/FeedbackContext';
import useOnlineStatus from 'hooks/useOnlineStatus';

import { MenuOrientation, isFullBleedPath } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// Mapping from therapist routes to their offline equivalents
const OFFLINE_ROUTE_MAP = {
  '/therapist/my-calendar': '/offline/my-calendar',
  '/therapist/data-collection': '/offline/data-entry'
};

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { isOnline } = useOnlineStatus();
  const wasOnlineRef = useRef(isOnline);

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // Routes that opt out of the boxed 'xl' width and span the full main area, the
  // way their mockups do. See FULL_BLEED_ROUTES in config — the nav is hidden on
  // these too (handled below and in the Drawer).
  const isFullBleed = isFullBleedPath(pathname);
  const boxed = container && !isFullBleed;

  // Auto-redirect to offline routes when internet drops
  useEffect(() => {
    if (wasOnlineRef.current && !isOnline) {
      // Find matching offline route (exact or prefix match for nested routes like /data-collection/:childId)
      const offlineRoute =
        OFFLINE_ROUTE_MAP[pathname] || Object.entries(OFFLINE_ROUTE_MAP).find(([prefix]) => pathname.startsWith(prefix + '/'))?.[1];

      if (offlineRoute) {
        navigate(offlineRoute, { replace: true });
      }
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline, pathname, navigate]);

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  // scroll to top on route change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  // Full-bleed consoles (Organizations and Centers) collapse the left nav on
  // entry so the page gets the whole width, mockup-style — only the hamburger is
  // left, and the user can reopen the nav with it. Leaving such a route restores
  // the nav to its normal, screen-size behaviour. Runs after the media-query
  // effect above so its collapse wins on a direct load of the route.
  const prevFullBleedRef = useRef(false);
  useEffect(() => {
    if (isFullBleed) {
      handlerDrawerOpen(false);
    } else if (prevFullBleedRef.current && !miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    prevFullBleedRef.current = isFullBleed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (menuMasterLoading) return <Loader />;

  return (
    <AuthGuard>
      {/* Inside AuthGuard: visibility is a per-user lookup, so there is no point
          resolving it for someone who is about to be sent to /login. */}
      <CatalogueGuard>
        <FeedbackProvider>
          <TourProvider>
            <GuidedTour />
            {/* Outside the shell, not inside a page: impersonation is a property
                of the session, so the reminder has to survive every route change.
                Renders a fixed bottom bar and a viewport frame, or nothing at all
                when not impersonating. */}
            <ImpersonationBanner />
            <Box sx={{ display: 'flex', width: '100%' }}>
              <Header />
              {!isHorizontal ? <Drawer /> : <HorizontalBar />}

              {/* Horizontal breathing room is set here and only here: the Container
                is gutter-less so its padding cannot stack on top of main's. The
                old code overrode Container's px only in boxed mode, so fluid mode
                silently inherited MUI's default 24px on top of these 24px. */}
              <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, py: { xs: 2, sm: 3 }, px: { xs: 1.25, sm: 2.5 } }}>
                <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
                <Container
                  maxWidth={boxed ? 'xl' : false}
                  disableGutters
                  sx={{
                    position: 'relative',
                    minHeight: 'calc(100vh - 110px)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {pathname !== '/apps/profiles/account/my-account' && <Breadcrumbs />}
                  <Outlet />
                  <Footer />
                </Container>
              </Box>
              <AddCustomer />
              <FeedbackWidget />
            </Box>
          </TourProvider>
        </FeedbackProvider>
      </CatalogueGuard>
    </AuthGuard>
  );
}
